import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Edit,
  Eye,
  Phone,
  Plus,
  RefreshCw,
  Save,
  Search,
  Trash2,
  X
} from 'lucide-react'
import { apiService } from '../../services/api'
import toast from 'react-hot-toast'
import { toastError } from '../../services/errorUtils'
import type { AgentOption, PhoneNumberOption } from '../../types/aicall'
import WhatsAppSetup from '../WhatsAppSetup/WhatsAppSetup'

interface SyncIssue {
  severity: 'error' | 'warning' | 'info'
  code: string
  message: string
}

interface TelephonySync {
  elevenlabs: {
    agents_count: number
    phone_numbers_count: number
  }
  evolution: {
    configured: boolean
    success: boolean
    missing: string[]
    instances_count: number
  }
  links: Array<{
    agent_id: string
    agent_name?: string
    phone_number_id?: string
    number?: string
    provider?: string
    supports_inbound?: boolean | null
    supports_outbound?: boolean | null
    outbound_trunk?: {
      address?: string | null
      transport?: string | null
      media_encryption?: string | null
      auth_username_masked?: string | null
    } | null
    evolution_instance?: {
      name?: string | null
      number?: string | null
      status?: string | null
      wavoip_token_masked?: string | null
      wavoip_device?: {
        success?: boolean
        status?: string | null
        phone?: string | null
        device_name?: string | null
        calls_count?: number | null
        ws_clients?: number | null
      } | null
    } | null
  }>
  issues: SyncIssue[]
}

interface PhoneNumberFormState {
  label: string
  phone_number: string
  agent_id: string
}

const emptyFormState: PhoneNumberFormState = {
  label: '',
  phone_number: '',
  agent_id: ''
}

export default function PhoneNumbers() {
  const [phoneNumbers, setPhoneNumbers] = useState<PhoneNumberOption[]>([])
  const [agents, setAgents] = useState<AgentOption[]>([])
  const [syncStatus, setSyncStatus] = useState<TelephonySync | null>(null)
  const [loading, setLoading] = useState(true)
  const [syncLoading, setSyncLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedPhone, setSelectedPhone] = useState<PhoneNumberOption | null>(null)
  const [showDetailsModal, setShowDetailsModal] = useState(false)
  const [showFormModal, setShowFormModal] = useState(false)
  const [editingPhoneId, setEditingPhoneId] = useState<string | null>(null)
  const [formState, setFormState] = useState<PhoneNumberFormState>(emptyFormState)
  const [showSetupGuide, setShowSetupGuide] = useState(false)


  const loadPhoneNumbers = useCallback(async () => {
    try {
      setLoading(true)
      const response = await apiService.getPhoneNumbers()
      setPhoneNumbers(response.data.data || [])
    } catch (error) {
      toastError(error, 'Erro ao carregar números de telefone')
    } finally {
      setLoading(false)
    }
  }, [])

  const loadAgents = useCallback(async () => {
    try {
      const response = await apiService.getAgents()
      setAgents(response.data.data || [])
    } catch (error) {
      console.error('Erro ao carregar agentes:', error)
    }
  }, [])

  const loadTelephonySync = useCallback(async () => {
    try {
      setSyncLoading(true)
      const response = await apiService.getTelephonySync()
      setSyncStatus(response.data.data || null)
    } catch (error) {
      toastError(error, 'Erro ao verificar sincronia de telefonia')
    } finally {
      setSyncLoading(false)
    }
  }, [])

  useEffect(() => {
    void Promise.all([loadPhoneNumbers(), loadAgents(), loadTelephonySync()])
  }, [loadAgents, loadPhoneNumbers, loadTelephonySync])

  const filteredPhoneNumbers = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase()
    if (!normalizedSearch) return phoneNumbers

    return phoneNumbers.filter((phone) =>
      [
        phone.number,
        phone.phone_number,
        phone.label,
        phone.name,
        phone.provider,
        phone.assigned_agent,
        phone.assigned_agent_id
      ].some((value) => String(value || '').toLowerCase().includes(normalizedSearch))
    )
  }, [phoneNumbers, searchTerm])

  const getStatusBadge = (status?: string) => {
    switch (status) {
      case 'active':
      case 'ready':
        return <span className="badge badge-success">Ativo</span>
      case 'inactive':
      case 'planned':
      case 'pending':
      case 'pending_whatsapp':
        return <span className="badge badge-warning">Em preparação</span>
      case 'error':
        return <span className="badge badge-danger">Erro</span>
      default:
        return <span className="badge badge-info">{status || 'N/A'}</span>
    }
  }

  const getSourceBadge = (phone: PhoneNumberOption) => {
    if (phone.source === 'elevenlabs') {
      return <span className="badge badge-info">ElevenLabs</span>
    }
    if (phone.source === 'provider_connection') {
      return <span className="badge">Provedor</span>
    }
    return null
  }

  const getPhoneDisplayNumber = (phone: PhoneNumberOption) => phone.number || phone.phone_number || 'N/A'
  const getPhoneDisplayName = (phone: PhoneNumberOption) => phone.label || phone.name || 'Sem nome'
  const getAgentName = (agentId?: string) =>
    agents.find((agent) => agent.id === agentId)?.name || agentId || 'Sem vínculo'

  const isManagedByProviderConnection = (phone: PhoneNumberOption) => phone.source === 'provider_connection'
  const isWavoipManaged = (phone: PhoneNumberOption) =>
    phone.source === 'wavoip_managed' || phone.provider === 'wavoip'

  const openDetails = (phone: PhoneNumberOption) => {
    setSelectedPhone(phone)
    setShowDetailsModal(true)
  }

  const openCreateModal = () => {
    setEditingPhoneId(null)
    setFormState(emptyFormState)
    setShowFormModal(true)
  }

  const openEditModal = (phone: PhoneNumberOption) => {
    if (isManagedByProviderConnection(phone) || isWavoipManaged(phone)) {
      toast('Esse número é gerenciado pela automação Wavoip/Evolution. Exclua em cascata e recrie se precisar trocar a conexão.', { duration: 6000 })
      return
    }

    setEditingPhoneId(phone.id)
    setFormState({
      label: getPhoneDisplayName(phone) === 'Sem nome' ? '' : getPhoneDisplayName(phone),
      phone_number: getPhoneDisplayNumber(phone) === 'N/A' ? '' : getPhoneDisplayNumber(phone),
      agent_id: phone.assigned_agent_id || ''
    })
    setShowFormModal(true)
  }

  const resetFormModal = () => {
    setShowFormModal(false)
    setEditingPhoneId(null)
    setFormState(emptyFormState)
  }

  const handleSubmit = async () => {
    const phone = formState.phone_number.trim()
    if (!phone) {
      toast.error('Informe o número de telefone.')
      return
    }
    if (!/^\+\d{7,15}$/.test(phone)) {
      toast.error('Use o formato internacional: +[código do país][número], ex: +5511999999999')
      return
    }

    try {
      setSubmitting(true)
      const payload = {
        phone_number: formState.phone_number.trim(),
        label: formState.label.trim() || undefined,
        name: formState.label.trim() || undefined,
        agent_id: formState.agent_id || undefined
      }

      if (editingPhoneId) {
        await apiService.updatePhoneNumber(editingPhoneId, payload)
        toast.success('Número atualizado com sucesso.')
      } else {
        await apiService.createPhoneNumber(payload)
        toast.success('Número criado com sucesso.')
      }

      resetFormModal()
      await Promise.all([loadPhoneNumbers(), loadTelephonySync()])
    } catch (error) {
      toastError(error, 'Erro ao salvar número')
    } finally {
      setSubmitting(false)
    }
  }

  const handleDelete = async (phone: PhoneNumberOption) => {
    if (isManagedByProviderConnection(phone) && !isWavoipManaged(phone)) {
      toast('Esse número é controlado pela conexão do provedor. Remova-o pela tela da integração correspondente.', {
        duration: 6000
      })
      return
    }

    if (!window.confirm(`Excluir o número ${getPhoneDisplayNumber(phone)}?`)) {
      return
    }

    try {
      setSubmitting(true)
      if (isWavoipManaged(phone)) {
        await apiService.deleteWavoipNumber(phone.id)
      } else {
        await apiService.deletePhoneNumber(phone.id)
      }
      toast.success('Número excluído com sucesso.')
      await Promise.all([loadPhoneNumbers(), loadTelephonySync()])
    } catch (error) {
      toastError(error, 'Erro ao excluir número')
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <div className="flex min-h-96 items-center justify-center">
        <RefreshCw className="h-10 w-10 animate-spin text-primary-600" />
      </div>
    )
  }

  return (
    <div className="page-shell">
      <div className="page-header">
        <div>
          <h1 className="page-title">Números</h1>
          <p className="page-description">
            Controle telefones, vínculos com agentes e a sincronia entre ElevenLabs e provedores de voz.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <WhatsAppSetup
            onComplete={() => void Promise.all([loadPhoneNumbers(), loadTelephonySync()])}
            trigger={(
              <button type="button" className="btn-primary">
                <Phone className="h-4 w-4" />
                WhatsApp automático
              </button>
            )}
          />
          <button type="button" className="btn-secondary" onClick={openCreateModal}>
            <Plus className="h-4 w-4" />
            Manual
          </button>
        </div>
      </div>

      {/* Setup guide */}
      <section className="surface">
        <button
          type="button"
          className="w-full flex items-center justify-between px-4 py-4 text-left hover:bg-slate-50 transition-colors rounded-t-lg"
          onClick={() => setShowSetupGuide(!showSetupGuide)}
          aria-expanded={showSetupGuide}
        >
          <div className="flex items-center gap-2">
            <Phone className="h-4 w-4 text-primary-600 flex-shrink-0" />
            <span className="text-sm font-semibold text-slate-700">Como adicionar e configurar um número (SIP trunk via Wavoip)</span>
          </div>
          {showSetupGuide
            ? <ChevronUp className="h-4 w-4 text-slate-400 flex-shrink-0" />
            : <ChevronDown className="h-4 w-4 text-slate-400 flex-shrink-0" />
          }
        </button>
        {showSetupGuide && (
          <div className="border-t border-slate-100 px-4 pb-4 pt-4 text-sm text-slate-700 space-y-4">
            <div>
              <p className="font-semibold text-slate-900 mb-2">Pré-requisitos</p>
              <ul className="list-disc ml-5 space-y-1 text-xs text-slate-600">
                <li>Conta ativa no <strong>Wavoip</strong> com saldo disponível</li>
                <li>Conta ElevenLabs com plano que suporte telefonia outbound</li>
                <li>Número brasileiro (ex: +5511...) vinculado ao device Wavoip</li>
              </ul>
            </div>
            <div>
              <p className="font-semibold text-slate-900 mb-2">Passo a passo</p>
              <ol className="list-decimal ml-5 space-y-2 text-xs text-slate-600">
                <li>
                  <strong>No painel Wavoip:</strong> crie um device SIP. Anote o <em>Token</em> do device. Em "Integrações &gt; SIP", deixe os campos Caller_ID, IP e Porta <strong>vazios</strong> e o transporte como <strong>UDP</strong>.
                </li>
                <li>
                  <strong>No painel ElevenLabs:</strong> vá em <em>Phone Numbers</em> e adicione um SIP trunk. Use o token do device Wavoip como usuário, senha e Caller ID. O servidor SIP é <code>sip.wavoip.com</code>.
                </li>
                <li>
                  <strong>Aqui no AICall:</strong> clique em <strong>Atualizar</strong> na tela de Sincronia — o número importado aparecerá automaticamente na lista.
                </li>
                <li>
                  <strong>Vincular ao agente:</strong> clique no número na lista, selecione o agente desejado e salve.
                </li>
                <li>
                  <strong>Testar:</strong> vá em Ligações &gt; Nova Ligação, selecione o agente e o número de destino (pode usar o mesmo número para teste).
                </li>
              </ol>
            </div>
            <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800">
              <strong>Importante:</strong> erros SIP 500 geralmente indicam configuração incorreta do tronco no ElevenLabs. Confira se usuário/senha/Caller ID estão corretos e se Caller_ID, IP e Porta estão <strong>vazios</strong> no Wavoip.
            </div>
          </div>
        )}
      </section>

      <section className="surface">
        <div className="panel-header">
          <div className="flex items-start gap-3">
            {syncStatus && syncStatus.issues.some((issue) => issue.severity === 'error') ? (
              <AlertTriangle className="mt-0.5 h-5 w-5 text-danger-600" />
            ) : (
              <CheckCircle2 className="mt-0.5 h-5 w-5 text-success-600" />
            )}
            <div>
              <h2 className="panel-title">Sincronia de telefonia</h2>
              <p className="panel-subtitle">Validação de agentes, números, tronco SIP e disponibilidade operacional.</p>
            </div>
          </div>
          <button onClick={() => void loadTelephonySync()} disabled={syncLoading} className="btn-secondary">
            <RefreshCw className={`h-4 w-4 ${syncLoading ? 'animate-spin' : ''}`} />
            Atualizar
          </button>
        </div>

        <div className="space-y-4 p-4 sm:p-5">
          <div className="grid gap-3 md:grid-cols-3">
            <SyncMetric
              label="ElevenLabs"
              value={syncStatus ? `${syncStatus.elevenlabs.agents_count} agentes / ${syncStatus.elevenlabs.phone_numbers_count} números` : 'Carregando'}
            />
            <SyncMetric
              label="Evolution"
              value={syncStatus?.evolution.configured ? `${syncStatus.evolution.instances_count} instâncias` : 'Não configurada'}
            />
            <SyncMetric label="Vínculos" value={syncStatus ? syncStatus.links.length : 0} />
          </div>

          {syncStatus && syncStatus.issues.length > 0 && (
            <div className="space-y-2">
              {syncStatus.issues.slice(0, 5).map((issue) => (
                <div
                  key={`${issue.code}-${issue.message}`}
                  className={`rounded-md border px-3 py-2 text-sm ${
                    issue.severity === 'error'
                      ? 'border-danger-200 bg-danger-50 text-danger-900'
                      : issue.severity === 'info'
                        ? 'border-primary-200 bg-primary-50 text-primary-900'
                        : 'border-warning-200 bg-warning-50 text-warning-900'
                  }`}
                >
                  <span className="break-all font-semibold">{issue.code}:</span>{' '}
                  <span className="break-words">{issue.message}</span>
                </div>
              ))}
            </div>
          )}

          {syncStatus && syncStatus.links.length > 0 && (
            <div className="grid gap-3 lg:grid-cols-2">
              {syncStatus.links.map((link) => (
                <div
                  key={`${link.agent_id}-${link.phone_number_id || link.number}`}
                  className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-3 text-xs text-slate-600"
                >
                  <div className="break-words text-sm font-semibold text-slate-950">{link.agent_name || link.agent_id}</div>
                  <div className="mt-1 break-words">
                    {link.number || link.phone_number_id} | {link.provider || 'provedor N/A'} | outbound{' '}
                    {link.supports_outbound ? 'ativo' : 'indefinido'}
                  </div>
                  {link.outbound_trunk?.address && (
                    <div className="mt-1 break-words">
                      Tronco: {link.outbound_trunk.address} ({link.outbound_trunk.transport || 'transporte N/A'})
                      {link.outbound_trunk.auth_username_masked ? ` | auth ${link.outbound_trunk.auth_username_masked}` : ''}
                    </div>
                  )}
                  {link.evolution_instance && (
                    <div className="mt-1 break-words">
                      Wavoip: {link.evolution_instance.name || link.evolution_instance.number || 'instância N/A'}
                      {link.evolution_instance.status ? ` (${link.evolution_instance.status})` : ''}
                      {link.evolution_instance.wavoip_token_masked ? ` | token ${link.evolution_instance.wavoip_token_masked}` : ''}
                    </div>
                  )}
                  {link.evolution_instance?.wavoip_device && (
                    <div className="mt-1 break-words">
                      Device: {link.evolution_instance.wavoip_device.status || 'status N/A'}
                      {link.evolution_instance.wavoip_device.phone ? ` | ${link.evolution_instance.wavoip_device.phone}` : ''}
                      {link.evolution_instance.wavoip_device.ws_clients != null
                        ? ` | ws ${link.evolution_instance.wavoip_device.ws_clients}`
                        : ''}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <div className="toolbar">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            aria-label="Buscar números"
            placeholder="Buscar números..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="input pl-10"
          />
        </div>
      </div>

      <section className="surface">
        <div className="panel-header">
          <div>
            <h2 className="panel-title">Números ({filteredPhoneNumbers.length})</h2>
            <p className="panel-subtitle">Telefones disponíveis para atendimento, SMS e chamadas de saída.</p>
          </div>
        </div>

        <div className="p-4 sm:p-5">
          {filteredPhoneNumbers.length === 0 ? (
            <div className="empty-state">
              <Phone className="mb-4 h-12 w-12 text-slate-300" />
              <h3 className="text-sm font-semibold text-slate-950">Nenhum número encontrado</h3>
              <p className="mt-1 text-sm text-slate-500">
                {searchTerm ? 'Tente ajustar a busca.' : 'Comece adicionando seu primeiro número.'}
              </p>
            </div>
          ) : (
            <>
              <div className="space-y-3 md:hidden">
                {filteredPhoneNumbers.map((phone) => (
                  <PhoneNumberCard
                    key={phone.id}
                    phone={phone}
                    agentName={getAgentName(phone.assigned_agent_id)}
                    getStatusBadge={getStatusBadge}
                    getSourceBadge={getSourceBadge}
                    onView={() => openDetails(phone)}
                    onEdit={() => openEditModal(phone)}
                    onDelete={() => void handleDelete(phone)}
                  />
                ))}
              </div>

              <div className="table-shell hidden md:block">
                <table className="min-w-[60rem] divide-y divide-slate-200">
                  <thead className="table-head">
                    <tr>
                      <th className="px-6 py-3 text-left">Nome</th>
                      <th className="px-6 py-3 text-left">Número</th>
                      <th className="px-6 py-3 text-left">Provedor</th>
                      <th className="px-6 py-3 text-left">Status</th>
                      <th className="px-6 py-3 text-left">Criado em</th>
                      <th className="px-6 py-3 text-right">Ações</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 bg-white">
                    {filteredPhoneNumbers.map((phone) => (
                      <tr key={phone.id} className="hover:bg-slate-50">
                        <td className="px-6 py-4">
                          <div className="break-words text-sm font-semibold text-slate-950">{getPhoneDisplayName(phone)}</div>
                          <div className="break-all text-xs font-mono text-slate-400 truncate max-w-[160px]">ID: {phone.id}</div>
                          {getSourceBadge(phone) && <div className="mt-1">{getSourceBadge(phone)}</div>}
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm font-semibold font-mono tabular-nums text-slate-950 truncate">{getPhoneDisplayNumber(phone)}</div>
                          <div className="text-xs text-slate-500 truncate">Agente: {getAgentName(phone.assigned_agent_id)}</div>
                          <div className="text-xs text-slate-500">
                            outbound {phone.supports_outbound ? 'ativo' : 'indefinido'} / SMS {phone.supports_sms ? 'ativo' : 'indefinido'}
                          </div>
                          {phone.reason && <div className="mt-1 text-xs text-warning-700">{phone.reason}</div>}
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-700">{phone.provider_label || phone.provider || 'N/A'}</td>
                        <td className="px-6 py-4">{getStatusBadge(phone.status)}</td>
                        <td className="px-6 py-4 text-sm text-slate-500 tabular-nums whitespace-nowrap">
                          {phone.created_at ? new Date(phone.created_at).toLocaleDateString('pt-BR') : 'N/A'}
                        </td>
                        <td className="px-6 py-4 text-right">
                          <ActionButtons
                            phone={phone}
                            onView={() => openDetails(phone)}
                            onEdit={() => openEditModal(phone)}
                            onDelete={() => void handleDelete(phone)}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </section>

      {showDetailsModal && selectedPhone && (
        <ModalShell title="Detalhes do número" onClose={() => setShowDetailsModal(false)} size="max-w-2xl">
          <div className="space-y-4">
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
              <dl className="grid gap-3 sm:grid-cols-2">
                <DetailRow label="Nome" value={getPhoneDisplayName(selectedPhone)} />
                <DetailRow label="Número" value={getPhoneDisplayNumber(selectedPhone)} />
                <DetailRow label="Provedor" value={selectedPhone.provider_label || selectedPhone.provider || 'N/A'} />
                <DetailRow label="Fonte" value={selectedPhone.source || 'N/A'} />
                <DetailRow label="Agente" value={getAgentName(selectedPhone.assigned_agent_id)} />
                <DetailRow label="Status" value={selectedPhone.status || 'N/A'} />
                <DetailRow label="Outbound" value={selectedPhone.supports_outbound ? 'Ativo' : 'Indefinido'} />
                <DetailRow label="SMS" value={selectedPhone.supports_sms ? 'Ativo' : 'Indefinido'} />
              </dl>
            </div>

            {selectedPhone.provider_connection_id && (
              <div className="rounded-lg border border-primary-100 bg-primary-50 p-4 text-sm text-primary-900">
                Esse número vem da conexão <strong>{selectedPhone.provider_connection_id}</strong>. Edição e remoção devem ser feitas na configuração do provedor.
              </div>
            )}

            {selectedPhone.reason && (
              <div className="rounded-lg border border-warning-200 bg-warning-50 p-4 text-sm text-warning-900">
                <strong>Motivo operacional:</strong> {selectedPhone.reason}
              </div>
            )}
          </div>
        </ModalShell>
      )}

      {showFormModal && (
        <ModalShell title={editingPhoneId ? 'Editar número' : 'Novo número'} onClose={resetFormModal} size="max-w-md">
          <div className="space-y-4">
            <div>
              <label className="mb-2 block text-sm font-medium text-slate-700">Nome</label>
              <input
                type="text"
                className="input"
                value={formState.label}
                onChange={(e) => setFormState((current) => ({ ...current, label: e.target.value }))}
                placeholder="Ex.: Comercial SDR"
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-slate-700">Número</label>
              <input
                type="tel"
                className="input"
                value={formState.phone_number}
                onChange={(e) => setFormState((current) => ({ ...current, phone_number: e.target.value }))}
                placeholder="+5511999999999"
              />
              <p className="mt-1 text-xs text-slate-500">Use formato internacional com código do país e DDD.</p>
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-slate-700">Agente vinculado</label>
              <select
                className="input"
                value={formState.agent_id}
                onChange={(e) => setFormState((current) => ({ ...current, agent_id: e.target.value }))}
              >
                <option value="">Sem vínculo</option>
                {agents.map((agent) => (
                  <option key={agent.id} value={agent.id}>
                    {agent.name || agent.agent_id || agent.id}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="mt-6 flex flex-col-reverse gap-3 border-t border-slate-100 bg-slate-50 px-4 py-4 sm:flex-row sm:justify-end sm:px-6">
            <button type="button" className="btn-secondary" onClick={resetFormModal}>
              Cancelar
            </button>
            <button type="button" className="btn-primary" disabled={submitting} onClick={() => void handleSubmit()}>
              {submitting ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              {editingPhoneId ? 'Salvar alterações' : 'Criar número'}
            </button>
          </div>
        </ModalShell>
      )}
    </div>
  )
}

function SyncMetric({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-3">
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</p>
      <p className="mt-1 break-words text-sm font-semibold text-slate-950">{value}</p>
    </div>
  )
}

function PhoneNumberCard({
  phone,
  agentName,
  getStatusBadge,
  getSourceBadge,
  onView,
  onEdit,
  onDelete
}: {
  phone: PhoneNumberOption
  agentName: string
  getStatusBadge: (status?: string) => ReactNode
  getSourceBadge: (phone: PhoneNumberOption) => ReactNode
  onView: () => void
  onEdit: () => void
  onDelete: () => void
}) {
  const displayNumber = phone.number || phone.phone_number || 'N/A'
  const displayName = phone.label || phone.name || 'Sem nome'

  return (
    <article className="card">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-slate-950 truncate">{displayName}</h3>
          <p className="mt-1 text-sm font-mono tabular-nums text-slate-600 truncate">{displayNumber}</p>
        </div>
        <div className="flex flex-col items-end gap-1 flex-shrink-0">
          {getStatusBadge(phone.status)}
          {getSourceBadge(phone)}
        </div>
      </div>
      <dl className="mt-3 grid gap-2 text-xs text-slate-600">
        <div className="flex justify-between gap-3">
          <dt>Provedor</dt>
          <dd className="font-semibold text-slate-900">{phone.provider_label || phone.provider || 'N/A'}</dd>
        </div>
        <div className="flex justify-between gap-3">
          <dt>Agente</dt>
          <dd className="truncate text-right font-semibold text-slate-900 max-w-[180px]">{agentName}</dd>
        </div>
        <div className="flex justify-between gap-3">
          <dt>Criado em</dt>
          <dd className="font-semibold text-slate-900 tabular-nums">
            {phone.created_at ? new Date(phone.created_at).toLocaleDateString('pt-BR') : 'N/A'}
          </dd>
        </div>
      </dl>
      {phone.reason && <p className="mt-3 text-xs text-warning-700">{phone.reason}</p>}
      <div className="mt-3 flex justify-end gap-2">
        <ActionButtons phone={phone} onView={onView} onEdit={onEdit} onDelete={onDelete} />
      </div>
    </article>
  )
}

function ActionButtons({
  phone,
  onView,
  onEdit,
  onDelete
}: {
  phone: PhoneNumberOption
  onView: () => void
  onEdit: () => void
  onDelete: () => void
}) {
  const isLocked = phone.source === 'provider_connection'

  return (
    <div className="flex items-center justify-end gap-1">
      <button className="btn-icon text-primary-600" title="Ver detalhes" aria-label={`Ver detalhes do número ${phone.id}`} onClick={onView}>
        <Eye className="h-4 w-4" />
      </button>
      <button
        className="btn-icon text-warning-600 disabled:cursor-not-allowed disabled:opacity-30"
        title={isLocked ? 'Edite na conexão do provedor' : 'Editar'}
        aria-label={`Editar número ${phone.id}`}
        disabled={isLocked}
        onClick={onEdit}
      >
        <Edit className="h-4 w-4" />
      </button>
      <button
        className="btn-icon text-danger-600 hover:text-danger-800 hover:bg-danger-50 disabled:cursor-not-allowed disabled:opacity-30"
        title={isLocked ? 'Remova na conexão do provedor' : 'Excluir'}
        aria-label={`Excluir número ${phone.id}`}
        disabled={isLocked}
        onClick={onDelete}
      >
        <Trash2 className="h-4 w-4" />
      </button>
    </div>
  )
}

function ModalShell({
  title,
  children,
  onClose,
  size = 'max-w-2xl'
}: {
  title: string
  children: ReactNode
  onClose: () => void
  size?: string
}) {
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center px-4 py-6 text-center sm:p-0">
        <div className="modal-backdrop" onClick={onClose} />
        <div className={`modal-panel relative ${size}`}>
          <div className="flex items-center justify-between border-b border-slate-100 px-4 py-4 sm:px-6">
            <h3 className="text-lg font-semibold text-slate-950">{title}</h3>
            <button type="button" onClick={onClose} className="btn-icon" aria-label={`Fechar modal ${title}`}>
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="px-4 py-4 text-left sm:px-6">{children}</div>
        </div>
      </div>
    </div>
  )
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-slate-100 bg-white p-3">
      <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</dt>
      <dd className="mt-1 break-words text-sm font-semibold text-slate-950">{value}</dd>
    </div>
  )
}
